import React from "react";
import { Form } from "react-bootstrap";
import {
  conditionMatches,
  getSessionContext,
  getVisibleFields,
  localizedText,
  localeKey,
} from "./schemaRuntime";
import "./formBuilder.css";

const DynamicRegistrationForm = ({
  definition,
  session,
  answers = {},
  onChange,
  language = "ind",
  errors = {},
}) => {
  if (!definition) return null;

  const schema = definition.registration_schema || {};
  const context = getSessionContext(definition, session, language);
  const fields = getVisibleFields(definition, session, answers, language);
  const blocks = (schema.blocks || [])
    .filter((block) => conditionMatches(block.visible_when, { answers, context }))
    .slice()
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  const pluginClass = `dynamic-registration-form--${definition.plugin_key || "standard"}`;

  const setValue = (key, value) => onChange && onChange(key, value);
  const renderBlock = (block, index) => {
    if (block.type === "image" && /^https:\/\//i.test(block.url || "")) {
      return (
        <img
          key={block.id || index}
          className="dynamic-registration-form__image"
          src={block.url}
          alt={localizedText(block.content, language, "Registration information")}
        />
      );
    }
    if (block.type === "section") {
      return (
        <h5 key={block.id || index} className="dynamic-registration-form__section-title">
          {localizedText(block.content, language)}
        </h5>
      );
    }
    return (
      <div key={block.id || index} className="dynamic-registration-form__info">
        {localizedText(block.content, language)}
      </div>
    );
  };

  const renderField = (field) => {
    const label = localizedText(field.label, language, field.key);
    const placeholder = localizedText(field.placeholder, language, "");
    const value = answers[field.key];
    const error = errors[field.key];
    const id = `dynamic-field-${field.key}`;
    const commonLabel = (
      <Form.Label>
        {label} {field.required ? <span className="text-danger">*</span> : null}
      </Form.Label>
    );

    if (field.type === "radio") {
      return (
        <Form.Group key={field.key} className="dynamic-registration-form__field">
          {commonLabel}
          {(field.options || []).map((option, index) => (
            <Form.Check
              key={String(option.value)}
              type="radio"
              id={`${id}-${index}`}
              name={id}
              required={field.required}
              checked={String(value == null ? "" : value) === String(option.value)}
              label={localizedText(option.label, language, option.value)}
              onChange={() => setValue(field.key, option.value)}
            />
          ))}
          {error ? <div className="invalid-feedback d-block">{error}</div> : null}
        </Form.Group>
      );
    }

    if (field.type === "checkbox") {
      return (
        <Form.Group key={field.key} className="dynamic-registration-form__field">
          <Form.Check
            type="checkbox"
            id={id}
            required={field.required}
            checked={!!value}
            label={label}
            onChange={(event) => setValue(field.key, event.target.checked)}
          />
          {error ? <div className="invalid-feedback d-block">{error}</div> : null}
        </Form.Group>
      );
    }

    if (field.type === "select") {
      return (
        <Form.Group key={field.key} className="dynamic-registration-form__field">
          {commonLabel}
          <Form.Control
            as="select"
            id={id}
            required={field.required}
            isInvalid={!!error}
            value={value == null ? "" : value}
            onChange={(event) => setValue(field.key, event.target.value)}
          >
            <option value="">{placeholder || "-- Select --"}</option>
            {(field.options || []).map((option) => (
              <option key={String(option.value)} value={option.value}>
                {localizedText(option.label, language, option.value)}
              </option>
            ))}
          </Form.Control>
          {error ? <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback> : null}
        </Form.Group>
      );
    }

    if (field.type === "multiselect") {
      const selected = Array.isArray(value) ? value : [];
      return (
        <Form.Group key={field.key} className="dynamic-registration-form__field">
          {commonLabel}
          {(field.options || []).map((option, index) => (
            <Form.Check
              key={String(option.value)}
              type="checkbox"
              id={`${id}-${index}`}
              checked={selected.includes(option.value)}
              label={localizedText(option.label, language, option.value)}
              onChange={(event) =>
                setValue(
                  field.key,
                  event.target.checked
                    ? [...selected, option.value]
                    : selected.filter((item) => item !== option.value)
                )
              }
            />
          ))}
          {error ? <div className="invalid-feedback d-block">{error}</div> : null}
        </Form.Group>
      );
    }

    return (
      <Form.Group key={field.key} className="dynamic-registration-form__field">
        {commonLabel}
        <Form.Control
          as={field.type === "textarea" ? "textarea" : "input"}
          rows={field.type === "textarea" ? 3 : undefined}
          type={field.type === "datetime" ? "datetime-local" : field.type}
          id={id}
          required={field.required}
          isInvalid={!!error}
          placeholder={placeholder}
          value={value == null ? "" : value}
          onChange={(event) => setValue(field.key, event.target.value)}
        />
        {error ? <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback> : null}
      </Form.Group>
    );
  };

  return (
    <div className={`dynamic-registration-form ${pluginClass}`}>
      {definition.plugin_key === "mission_trip" &&
      definition.config &&
      /^https:\/\//i.test(definition.config.banner_url || "") ? (
        <img
          className="dynamic-registration-form__image dynamic-registration-form__plugin-image"
          src={definition.config.banner_url}
          alt={context.package_label || context.session_name}
        />
      ) : null}
      {definition.plugin_key === "mission_trip" ? (
        <div className="dynamic-registration-form__plugin-header">
          <strong>{localeKey(language) === "en" ? "Selected package" : "Paket dipilih"}</strong>
          <span>{context.package_label || context.session_name}</span>
        </div>
      ) : null}
      {blocks.map(renderBlock)}
      {fields.map(renderField)}
    </div>
  );
};

export default DynamicRegistrationForm;
